import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Empty, Tag, Typography } from 'antd';
import { UndoOutlined } from '@ant-design/icons';

import useDateFormat from '../../hooks/useDateFormat';
import { formatDisplayDate, formatDisplayDateTime } from '../../utils/dateDisplay';
import { formatRelativeToNow } from '../../utils/formatters';
import { actorLabel, isItemDeletion, TYPE_LABELS } from '../../utils/history';
import { HISTORY } from '../icons';
import { DataState, Skeleton, deriveDataState } from '../loading';
import DeletedItemHistoryModal from './DeletedItemHistoryModal';
import RevertChangeModal from './RevertChangeModal';

const { Text } = Typography;

const PAGE = 50;

/**
 * "Recently deleted" (delete audit F5) — one list for the three places it
 * appears: a collection's Settings tab (its deleted cards, element sets and
 * items; managers), the Collections page's own list (the caller's deleted
 * collections; owners), and Admin → Recently deleted (everything).
 *
 * Each row is what was deleted, when, by whom, and until when it can come
 * back, with **Restore** — which is the history revert of the delete entry
 * (RevertChangeModal in `restore` mode). There is no restore logic here or
 * on the API beyond the revert, so a refusal reads in the revert's own
 * words: a row the list already knows is blocked (an item whose card is
 * deleted) says why and offers no button; one the revert refuses on the
 * day says why in the modal.
 *
 * An ITEM's row also offers **View history**: the item has no page while it
 * is deleted, so its row is the door to its whole history, read-only
 * (DeletedItemHistoryModal) — what it said, who changed it, and its
 * deletion as the newest entry.
 *
 * `fetchPage({ limit, skip })` → { rows, total } (services/history.js).
 * `showCollection` adds the collection a row was in (the admin list).
 */
export default function RecentlyDeleted({ fetchPage, emptyText = 'Nothing has been deleted.', showCollection = false, onRestored }) {
	useDateFormat();
	const [rows, setRows] = useState([]);
	const [total, setTotal] = useState(0);
	const [loading, setLoading] = useState(true);
	const [loadingMore, setLoadingMore] = useState(false);
	const [error, setError] = useState(null);
	const [moreError, setMoreError] = useState(false);
	// True once a first read has answered, so a restore's reload is a refresh and keeps the rows on screen.
	const [loaded, setLoaded] = useState(false);
	const [restoring, setRestoring] = useState(null);
	const [historyOf, setHistoryOf] = useState(null);

	const load = useCallback(async () => {
		setLoading(true);
		try {
			const page = await fetchPage({ limit: PAGE, skip: 0 });
			setRows(page.rows);
			setTotal(page.total);
			setError(null);
			setMoreError(false);
			setLoaded(true);
		} catch (e) {
			setError(e);
		} finally {
			setLoading(false);
		}
	}, [fetchPage]);

	useEffect(() => { load(); }, [load]);

	const more = async () => {
		setLoadingMore(true);
		setMoreError(false);
		try {
			const page = await fetchPage({ limit: PAGE, skip: rows.length });
			setRows(prev => [...prev, ...page.rows]);
			setTotal(page.total);
		} catch (e) {
			setMoreError(true);
		} finally {
			setLoadingMore(false);
		}
	};

	const status = error?.response?.status;
	if (error && !loaded && (status === 403 || status === 404)) {
		return <Alert type="error" showIcon message="You can't see what was deleted here." />;
	}

	return (
		<DataState
			state={deriveDataState({ loading, error: loading ? null : error, hasData: loaded, isEmpty: loaded && !rows.length })}
			hasData={loaded}
			isEmpty={loaded && !rows.length}
			skeleton={<Skeleton variant="list-row" count={4} />}
			empty={<Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={emptyText} />}
			onRetry={load}
			label="what was deleted"
		>
			<div className="recently-deleted">
				<ul className="recently-deleted__rows" aria-label="Recently deleted">
					{rows.map(row => (
						<DeletedRow
							key={row._id}
							row={row}
							showCollection={showCollection}
							onRestore={() => setRestoring(row)}
							onHistory={isItemDeletion(row) ? () => setHistoryOf(row) : null}
						/>
					))}
				</ul>
				{moreError ? <Text type="danger" role="alert">Unable to load more.</Text> : null}
				{rows.length < total ? (
					<Button className="recently-deleted__more" onClick={more} loading={loadingMore}>Show more</Button>
				) : null}
				<DeletedItemHistoryModal entry={historyOf} onClose={() => setHistoryOf(null)} />
				<RevertChangeModal
					open={Boolean(restoring)}
					entry={restoring}
					restore
					onClose={() => setRestoring(null)}
					onReverted={() => {
						load();
						if (typeof onRestored === 'function') onRestored(restoring);
					}}
				/>
			</div>
		</DataState>
	);
}

/** When it stops being restorable, as a person reads it. */
export function restorableLine(row) {
	if (row?.restorableUntil) return `Restorable until ${formatDisplayDate(row.restorableUntil)}`;
	return 'Restorable while its history is kept';
}

function DeletedRow({ row, showCollection, onRestore, onHistory = null }) {
	const type = row.target?.type;
	const kind = TYPE_LABELS[type] || type || '';
	const label = row.target?.label || `Untitled ${kind.toLowerCase()}`;
	const when = row.at ? formatDisplayDateTime(row.at) : '';
	const blocked = row.blocked || null;
	const withCounts = row.deletedWith;
	return (
		<li className="recently-deleted__row" data-type={type}>
			<div className="recently-deleted__what">
				<span className="micro-label recently-deleted__kind">{row.target?.card || kind}</span>
				<span className="recently-deleted__title">{label}</span>
				{showCollection && row.collection?.name && type !== 'collection' ? (
					<Text type="secondary" className="recently-deleted__in">in {row.collection.name}</Text>
				) : null}
			</div>
			<div className="recently-deleted__meta">
				<span>
					Deleted <time dateTime={row.at} title={when}>{formatRelativeToNow(row.at)}</time> by {actorLabel(row.actor)}
				</span>
				<span className="recently-deleted__until">
					{row.restorableUntil ? <Tag color="gold">{restorableLine(row)}</Tag> : restorableLine(row)}
				</span>
				{withCounts ? (
					<span>
						With its owner&apos;s account: {withCounts.items ?? 0} item{withCounts.items === 1 ? '' : 's'}
						{withCounts.itemsByOthers ? `, ${withCounts.itemsByOthers} by other people` : ''}
					</span>
				) : null}
			</div>
			{blocked ? <Text type="warning" className="recently-deleted__blocked">{blocked.message}</Text> : null}
			<div className="recently-deleted__actions">
				{onHistory ? (
					<Button
						size="small"
						icon={<HISTORY />}
						aria-label={`View history of ${label}`}
						onClick={onHistory}
					>
						View history
					</Button>
				) : null}
				<Button
					size="small"
					icon={<UndoOutlined />}
					disabled={Boolean(blocked) || row.revertable === false}
					aria-label={`Restore ${label}`}
					onClick={onRestore}
				>
					Restore
				</Button>
			</div>
		</li>
	);
}
